import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Rating,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

const testimonials = [
  {
    name: 'Sarah Johnson',
    location: 'New York, USA',
    rating: 5,
    text: 'Absolutely amazing experience! The tour was perfectly organized, and the guide was incredibly knowledgeable.',
    trip: 'European Grand Tour',
  },
  {
    name: 'Michael Chen',
    location: 'Toronto, Canada',
    rating: 5,
    text: 'Best travel agency I have ever worked with. They customized our itinerary perfectly and the prices were very reasonable!',
    trip: 'Southeast Asia Adventure',
  },
  {
    name: 'Emma Williams',
    location: 'London, UK',
    rating: 5,
    text: 'From booking to return, everything was seamless. The hotel choices were excellent and the local experiences were unforgettable.',
    trip: 'Maldives Getaway',
  },
];

const Testimonials = ({ variant = 'default' }) => {
  if (variant === 'compact') {
    return (
      <Box sx={{ py: 3 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <FormatQuoteIcon sx={{ color: '#667eea', fontSize: 20 }} />
          What Travelers Say
        </Typography>
        <Grid container spacing={2}>
          {testimonials.slice(0, 2).map((testimonial, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 2.5,
                  bgcolor: '#fff',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <FormatQuoteIcon sx={{ color: 'primary.main', opacity: 0.2, fontSize: 22 }} />
                    <Rating
                      value={testimonial.rating}
                      readOnly
                      size="small"
                      icon={<StarIcon sx={{ color: '#fbbf24' }} fontSize="inherit" />}
                      emptyIcon={<StarIcon fontSize="inherit" />}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6, mb: 2, fontStyle: 'italic', fontSize: '0.82rem' }}
                  >
                    &ldquo;{testimonial.text}&rdquo;
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: 'primary.main',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                      }}
                    >
                      {testimonial.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {testimonial.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {testimonial.trip}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#ffffff', py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '2rem', color: 'text.primary' }}>
            What Our Travelers Say
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real feedback from travelers who experienced our curated tours
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  bgcolor: '#fff',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FormatQuoteIcon sx={{ color: 'primary.main', opacity: 0.3, fontSize: 28 }} />
                    <Rating
                      value={testimonial.rating}
                      readOnly
                      size="small"
                      icon={<StarIcon sx={{ color: '#fbbf24' }} fontSize="inherit" />}
                      emptyIcon={<StarIcon fontSize="inherit" />}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.7, mb: 3, fontStyle: 'italic' }}
                  >
                    &ldquo;{testimonial.text}&rdquo;
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: 'primary.main',
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      {testimonial.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {testimonial.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {testimonial.location}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      display: 'inline-block',
                      bgcolor: 'rgba(102,126,234,0.08)',
                      color: 'primary.main',
                      px: 1.5,
                      py: 0.4,
                      borderRadius: 50,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {testimonial.trip}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Testimonials;